import { NetworkStatus, useQuery } from "@apollo/client";
import Avatar from "components/core/Avatar";
import IndividualCommentInputForm from "components/discussions/form/IndividualCommentInputForm";
import PostCommentOnAssetMutation from "components/discussions/mutations/PostCommentOnAssetMutation";
import NavbarLayout from "components/layouts/NavbarLayout";
import PageHeading from "components/page/PageHeading";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import RelativeTime from "components/ui/RelativeTime";
import classNames from "components/ui/classNames";
import lightFormat from "date-fns/lightFormat";
import parseISO from "date-fns/parseISO";
import subMonths from "date-fns/subMonths";
import useSelfie from "lib/hooks/useSelfie";
import CvmMaterialFactsQuery from "lib/queries/CvmMaterialFactsQuery";
import type {
  ICvmMaterialFactsQuery,
  ICvmMaterialFactsQueryVariables,
} from "lib/queries/__generated__/CvmMaterialFactsQuery.generated";
import getQueryStringValue from "lib/utils/getQueryStringValue";
import translateTypenameIntoAssetType from "lib/utils/translateTypenameIntoAssetType";
import capitalize from "lodash/capitalize";
import { NewspaperIcon } from "lucide-react";
import type { NextPage } from "next";
import Link from "next/link";
import { useRouter } from "next/router";
import { Value } from "react-powerplug";
import { ICvmMaterialFactIssuersFilter } from "types/graphqlTypes";

interface FatosRelevantesPageProps {
  filterBy: string | null;
}

const FatosRelevantes: NextPage<FatosRelevantesPageProps> = function FatosRelevantes({ filterBy }) {
  const selfie = useSelfie();
  return (
    <NavbarLayout>
      <FundamenteiMetadata
        title="Fatos Relevantes | Fundamentei"
        description="Os Fatos Relevantes dos últimos 90 dias publicados oficialmente na CVM."
      />

      <div className="mx-auto max-w-7xl px-4 pb-8 xl:px-0">
        <PageHeading
          title="Fatos Relevantes"
          description="Confira abaixo os Fatos Relevantes dos últimos 90 dias publicados oficialmente na CVM."
          icon={<NewspaperIcon strokeWidth={1.5} className="h-6 w-6 fill-transparent" />}
          iconContainerClassName="border-green-500 bg-emerald-50 text-emerald-500"
        />

        {selfie && (
          <div className="mb-8">
            <Filters filterBy={filterBy} />
          </div>
        )}

        <div className="mb-4">
          <MaterialFacts filterBy={filterBy} />
        </div>
      </div>
    </NavbarLayout>
  );
};

FatosRelevantes.getInitialProps = async ({ query }) => ({
  filterBy: getQueryStringValue(query, "filterBy"),
});

export default FatosRelevantes;

interface IMaterialFactProps {
  materialFact: ICvmMaterialFactsQuery["cvmMaterialFacts"]["nodes"][0];
  isReplying: boolean;
  onToggleReplying: (materialFact: ICvmMaterialFactsQuery["cvmMaterialFacts"]["nodes"][0] | null) => void;
}

function MaterialFact({ materialFact, isReplying, onToggleReplying }: IMaterialFactProps) {
  const selfie = useSelfie();
  const router = useRouter();
  if (!materialFact.issuer) {
    return null;
  }
  return (
    <div className="py-6 first:pt-0 last:pb-0">
      <div
        className={classNames("font-aeonik font-normal", {
          "rounded-xl border border-violet-200 bg-white p-4 shadow-md dark:border-slate-950 dark:bg-slate-950/30 lg:rounded-2xl":
            isReplying,
        })}
      >
        <div key={materialFact.protocol} className={classNames("flex items-start space-x-4")}>
          <Link
            as={`/br/${materialFact.issuer.primaryTickerSymbol.toLowerCase()}`}
            href="/br/[tickerSymbol]"
            passHref={true}
            legacyBehavior={true}
          >
            <Avatar
              src={materialFact.issuer.niceLogo?.url || null}
              hashValue={materialFact.issuer._id}
              alt={materialFact.issuer.name}
              size={40}
              squircle={true}
            />
          </Link>

          <div className="flex flex-col space-y-1">
            <div>
              <Link
                as={`/br/${materialFact.issuer.primaryTickerSymbol.toLowerCase()}`}
                href="/br/[tickerSymbol]"
                passHref={true}
                className="text-md m-0 inline-flex items-center space-x-2 font-medium text-zinc-500 hover:underline dark:font-medium dark:text-zinc-400"
              >
                {materialFact.issuer.name}
              </Link>
            </div>

            <div>
              <a
                href={materialFact.url}
                rel="noopener noreferrer"
                target="_blank"
                title={materialFact.subjects.length > 0 ? noScream(materialFact.subjects[0]) : undefined}
                className={classNames(
                  "line-clamp-3 max-w-3xl text-lg font-medium text-blue-500 dark:text-blue-400",
                  "visited:flex visited:text-purple-600",
                  "hover:underline",
                  "lg:line-clamp-2",
                )}
              >
                {materialFact.subjects.length > 0 ? noScream(materialFact.subjects[0]) : materialFact.category}
              </a>
            </div>

            <div className="flex items-center text-gray-500 dark:text-gray-400">
              <RelativeTime className="text-sm font-normal" timeStamp={parseISO(materialFact.deliveryDate)} />
            </div>

            {selfie && selfie.isPremium && (
              <div>
                <button
                  type="button"
                  className={classNames(
                    "mt-2 rounded-lg border bg-white px-3 py-1 text-sm text-gray-500 shadow",
                    "font-medium hover:border-transparent hover:bg-brand hover:text-white",
                    "dark:border-transparent dark:bg-teal-600/30 dark:text-teal-300 dark:hover:bg-teal-600/20",
                    { hidden: isReplying },
                  )}
                  onClick={() => onToggleReplying(materialFact)}
                >
                  Comentar
                </button>
              </div>
            )}
          </div>
        </div>

        {isReplying && (
          <>
            <div className="mt-4" />

            <div className="mb-2 mt-6">
              <PostCommentOnAssetMutation>
                {(postCommentOnAsset) => (
                  <IndividualCommentInputForm
                    submitButtonText="Comentar"
                    autoFocus={true}
                    onSubmit={async (bodyAsPlainText) => {
                      if (materialFact.issuer) {
                        const mutationResult = await postCommentOnAsset({
                          assetType: translateTypenameIntoAssetType(materialFact.issuer.__typename),
                          assetId: materialFact.issuer?._id,
                          bodyAsPlainText,
                        });
                        if (
                          mutationResult &&
                          mutationResult.data &&
                          mutationResult.data.postCommentOnAsset &&
                          mutationResult.data.postCommentOnAsset.shortId
                        ) {
                          router.push(
                            "/comments/[shortId]",
                            `/comments/${mutationResult.data?.postCommentOnAsset.shortId}`,
                          );
                        }
                      }

                      return null;
                    }}
                    onCancel={() => onToggleReplying(null)}
                    bodyAsPlainText={
                      materialFact.subjects.length > 0
                        ? `⚠️ Fato Relevante: ${noScream(materialFact.subjects[0])}\n\n🔗 Link: ${materialFact.url}\n\n`
                        : `🔗 ${materialFact.url}\n\n`
                    }
                  />
                )}
              </PostCommentOnAssetMutation>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MaterialFactSkeleton() {
  return (
    <div className="flex animate-pulse items-start space-x-4">
      <div className="h-10 w-10 rounded-full bg-slate-200 squircle dark:bg-slate-700" />

      <div className="flex-1 space-y-2">
        <div className="h-3 w-28 rounded bg-slate-200 dark:bg-slate-700" />
        <div className="h-3 w-3/4 rounded bg-slate-200 dark:bg-slate-700" />
        <div className="h-2 w-2/3 rounded bg-slate-200 dark:bg-slate-700" />
        <div className="h-2 w-1/3 rounded bg-slate-200 dark:bg-slate-700" />
      </div>
    </div>
  );
}

interface IMaterialFactsProps {
  filterBy: string | null;
}

function MaterialFacts({ filterBy }: IMaterialFactsProps) {
  const { data, loading, fetchMore, networkStatus } = useQuery<ICvmMaterialFactsQuery, ICvmMaterialFactsQueryVariables>(
    CvmMaterialFactsQuery,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        deliveryDateStart: lightFormat(subMonths(new Date(), 6), "yyyy-MM-dd"),
        deliveryDateEnd: lightFormat(new Date(), "yyyy-MM-dd"),
        filterByCategories: ["Fato Relevante"],
        filterByIssuers: filterBy ? (filterBy as ICvmMaterialFactIssuersFilter) : null,
        filterByIssuerCnpj: null,
        cursor: null,
        limit: 20,
      },
    },
  );
  const isLoadingMore = networkStatus === NetworkStatus.fetchMore;
  if (loading && !isLoadingMore) {
    return (
      <div className="space-y-12">
        {Array.from({ length: 6 }).map((_, index) => {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <MaterialFactSkeleton key={index} />
          );
        })}
      </div>
    );
  }

  if (data?.cvmMaterialFacts.totalCount === 0) {
    return (
      <div className="space-y-2 rounded-xl bg-slate-100 p-6 font-aeonik font-normal text-slate-500 dark:bg-slate-950/30 dark:text-gray-400">
        <h2 className="text-2xl font-bold text-slate-600 dark:text-slate-300">Nenhum Fato Relevante encontrado...</h2>
        <p>Verifique os filtros e tente novamente.</p>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      <div className="divide-y divide-slate-200 dark:divide-slate-700/40">
        <Value<number | null> initial={null}>
          {({ value: replyingToMaterialFactProtocol, set: setIsReplyingToMaterialFactProtocol }) => {
            return data?.cvmMaterialFacts.nodes.map((materialFact) => {
              return (
                <MaterialFact
                  key={materialFact.protocol}
                  materialFact={materialFact}
                  isReplying={materialFact.protocol === replyingToMaterialFactProtocol}
                  onToggleReplying={(materialFact) => {
                    setIsReplyingToMaterialFactProtocol(materialFact ? materialFact.protocol : null);
                  }}
                />
              );
            });
          }}
        </Value>

        {isLoadingMore && (
          <div className="py-6 first:pt-0 last:pb-0">
            <MaterialFactSkeleton />
          </div>
        )}
      </div>

      {data?.cvmMaterialFacts.pageInfo.hasNextPage && (
        <div className="text-center">
          <button
            disabled={loading || isLoadingMore}
            type="button"
            className={classNames(
              "font-aeonik text-base font-medium tracking-wide text-blue-600 hover:underline",
              "disabled:text-gray-500 dark:text-blue-200",
            )}
            onClick={() => {
              if (data?.cvmMaterialFacts.pageInfo) {
                fetchMore({
                  variables: {
                    cursor: data?.cvmMaterialFacts.pageInfo.cursor,
                    limit: 10,
                  },
                  updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                    if (!fetchMoreResult || !fetchMoreResult.cvmMaterialFacts) {
                      return previousQueryResult;
                    }
                    return {
                      ...previousQueryResult,
                      cvmMaterialFacts: {
                        ...fetchMoreResult.cvmMaterialFacts,
                        nodes: previousQueryResult.cvmMaterialFacts.nodes.concat(
                          fetchMoreResult.cvmMaterialFacts.nodes,
                        ),
                      },
                    };
                  },
                });
              }
            }}
          >
            Carregar mais Fatos Relevantes
          </button>
        </div>
      )}
    </div>
  );
}

function noScream(text: string) {
  // Check if everything's in uppercase, if so, then convert it to title case
  return text === text.toUpperCase() ? capitalize(text) : text;
}

interface IFiltersProps {
  filterBy: string | null;
}

function Filters({ filterBy }: IFiltersProps) {
  const router = useRouter();
  return (
    <div className="flex flex-wrap gap-2 md:flex-nowrap">
      {[
        {
          filterBy: ICvmMaterialFactIssuersFilter.InPortfolio,
          title: "Minha Carteira",
          accessibilityDescription: "Filtrar os Fatos Relevantes da sua carteira",
        },
        {
          filterBy: ICvmMaterialFactIssuersFilter.InBookmarks,
          title: "Meus Favoritos",
          accessibilityDescription: "Filtrar os Fatos Relevantes dos seus Favoritos",
        },
      ].map((node) => {
        const isActive = filterBy === node.filterBy;
        return (
          <button
            key={node.filterBy}
            type="button"
            aria-label={node.accessibilityDescription}
            className={classNames(
              "font-aeonik text-sm font-medium",
              "border border-gray-200",
              "rounded-full bg-gray-100 px-4 py-2 ",
              "text-gray-500",
              "dark:border-transparent dark:bg-slate-900 dark:text-gray-400 dark:hover:bg-slate-950 dark:hover:text-gray-200",
              classNames({
                "hover:border-violet-200 hover:bg-violet-50 hover:text-violet-600": !isActive,
                "border-transparent bg-violet-600 text-white dark:bg-violet-600/20 dark:text-violet-200": isActive,
              }),
            )}
            onClick={() => {
              router.push(
                filterBy === node.filterBy ? `/fatos-relevantes` : `/fatos-relevantes?filterBy=${node.filterBy}`,
              );
            }}
          >
            {node.title}
          </button>
        );
      })}
    </div>
  );
}
